﻿<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @mousedown.self="$emit('close')">
    <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div
        class="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          {{
            currentStep === 1
              ? tt('Create function', 'Ustvari funkcijo')
              : `${tt('Configure', 'Nastavi')} ${selectedType?.name}`
          }}
        </h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <!-- Step Indicator -->
      <div class="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div class="flex items-center gap-4">
          <div
            class="flex items-center gap-2"
            :class="currentStep >= 1 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'">
            <span
              class="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium"
              :class="currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 dark:bg-gray-600'"
              >1</span
            >
            <span class="text-sm font-medium">{{ tt('Select Type', 'Izberi vrsto') }}</span>
          </div>
          <div class="flex-1 h-0.5 bg-gray-300 dark:bg-gray-600" :class="currentStep >= 2 ? 'bg-blue-600' : ''"></div>
          <div
            class="flex items-center gap-2"
            :class="currentStep >= 2 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'">
            <span
              class="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium"
              :class="currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 dark:bg-gray-600'"
              >2</span
            >
            <span class="text-sm font-medium">{{ tt('Configure', 'Nastavi') }}</span>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Step 1: Select Automation Type -->
        <div v-if="currentStep === 1" class="space-y-4">
          <p class="text-gray-600 dark:text-gray-400">
            {{
              tt(
                'Select the type of automation pattern to create:',
                'Izberite vrsto vzorca avtomatizacije, ki ga želite ustvariti:',
              )
            }}
          </p>

          <div v-if="loadingTypes" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              v-for="type in automationTypes"
              :key="type.automationType"
              @click="selectType(type)"
              class="p-4 border rounded-lg text-left transition-colors"
              :class="
                selectedType?.automationType === type.automationType
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              ">
              <div class="flex items-start gap-3">
                <span class="material-symbols-outlined text-2xl text-blue-500 dark:text-blue-400">
                  {{ getTypeIcon(type).icon }}
                </span>
                <div class="flex-1">
                  <h3 class="font-semibold text-gray-900 dark:text-white">{{ type.name }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ type.description }}</p>
                  <div class="flex flex-wrap gap-1 mt-2">
                    <span
                      v-for="role in type.requiredBindings"
                      :key="role"
                      class="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                      {{ formatBindingRole(role) }}
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <!-- Step 2: Configure Automation -->
        <div v-if="currentStep === 2 && selectedType" class="space-y-6">
          <!-- Basic Info -->
          <div class="space-y-4">
            <h3 class="font-semibold text-gray-900 dark:text-white">
              {{ tt('Basic Information', 'Osnovni podatki') }}
            </h3>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {{ tt('Function name', 'Ime funkcije') }} *
              </label>
              <input
                v-model="form.name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                :placeholder="tt('e.g., Kitchen Motion Light', 'npr. Kuhinjska luč ob gibu')" />
            </div>
            <div class="flex items-center gap-3">
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" v-model="form.enabled" class="sr-only peer" />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
              <span class="text-sm text-gray-700 dark:text-gray-300">{{
                tt('Enable function', 'Omogoči funkcijo')
              }}</span>
            </div>
          </div>

          <!-- Bindings -->
          <div class="space-y-4">
            <h3 class="font-semibold text-gray-900 dark:text-white">{{ tt('Device Bindings', 'Povezave naprav') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{
                tt(
                  'Connect device properties to this automation pattern.',
                  'Povežite lastnosti naprav s tem vzorcem avtomatizacije.',
                )
              }}
            </p>

            <!-- Required Bindings -->
            <template v-for="role in selectedType.requiredBindings" :key="role">
              <!-- Multi-binding role (zones) -->
              <div v-if="role === 'zone'" class="space-y-3">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{ formatBindingRolePlural(role) }} *
                  </label>
                  <button
                    @click="addZone"
                    type="button"
                    class="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1">
                    <span class="material-symbols-outlined text-sm">add</span>
                    {{ tt('Add Zone', 'Dodaj cono') }}
                  </button>
                </div>
                <div v-for="(zone, index) in zoneBindings" :key="index" class="flex items-center gap-2">
                  <span class="text-sm text-gray-500 dark:text-gray-400 w-8">{{ index + 1 }}.</span>
                  <div class="flex-1">
                    <DeviceValuePicker
                      v-model="zoneBindings[index]"
                      :title="`${tt('Select Zone', 'Izberi cono')} ${index + 1}`"
                      :filter-device-id="isLocalOnly('zone') ? props.deviceId : undefined" />
                  </div>
                  <button
                    v-if="zoneBindings.length > 1"
                    @click="removeZone(index)"
                    type="button"
                    class="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded">
                    <span class="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    tt(
                      'Zones will be watered sequentially in the order listed above.',
                      'Cone se zalivajo zaporedoma v zgoraj navedenem vrstnem redu.',
                    )
                  }}
                </p>
              </div>

              <!-- Multi-binding role (heat sources) -->
              <div v-else-if="role === 'heat_source'" class="space-y-3">
                <div class="flex items-center justify-between">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{ tt('Thermostat Outputs', 'Izhodi termostata') }} *
                  </label>
                  <button
                    @click="addHeatSource"
                    type="button"
                    class="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors flex items-center gap-1">
                    <span class="material-symbols-outlined text-sm">add</span>
                    {{ tt('Add Zone', 'Dodaj cono') }}
                  </button>
                </div>
                <div v-for="(src, index) in heatSourceBindings" :key="index" class="flex items-center gap-2">
                  <span class="text-sm text-gray-500 dark:text-gray-400 w-8">{{ index + 1 }}.</span>
                  <div class="flex-1">
                    <DeviceValuePicker
                      v-model="heatSourceBindings[index]"
                      :title="`${tt('Select Thermostat Output', 'Izberi izhod termostata')} ${index + 1}`"
                      :filter-device-id="isLocalOnly('heat_source') ? props.deviceId : undefined" />
                  </div>
                  <button
                    v-if="heatSourceBindings.length > 1"
                    @click="removeHeatSource(index)"
                    type="button"
                    class="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded">
                    <span class="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    tt(
                      'Heat demand activates when any zone output exceeds the threshold.',
                      'Potreba po ogrevanju se aktivira, ko kateri koli izhod cone preseže prag.',
                    )
                  }}
                </p>
              </div>

              <!-- Single binding role -->
              <div v-else class="space-y-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {{ formatBindingRole(role) }} *
                </label>
                <DeviceValuePicker
                  v-model="bindings[role]"
                  :title="`${tt('Select', 'Izberi')} ${formatBindingRole(role)}`"
                  :filter-device-id="isLocalOnly(role) ? props.deviceId : undefined" />
              </div>
            </template>

            <!-- Optional Bindings -->
            <div v-for="role in selectedType.optionalBindings" :key="role" class="space-y-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ formatBindingRole(role) }} <span class="text-gray-400">{{ tt('(optional)', '(neobvezno)') }}</span>
              </label>
              <DeviceValuePicker
                v-model="bindings[role]"
                :title="`${tt('Select', 'Izberi')} ${formatBindingRole(role)}`"
                :filter-device-id="isLocalOnly(role) ? props.deviceId : undefined" />
              <p v-if="bindingDescription(role)" class="text-xs text-gray-500 dark:text-gray-400">
                {{ bindingDescription(role) }}
              </p>
              <button
                v-if="bindings[role]"
                @click="bindings[role] = null"
                type="button"
                class="text-xs text-red-600 dark:text-red-400 hover:underline">
                {{ tt('Remove binding', 'Odstrani povezavo') }}
              </button>
            </div>
          </div>

          <!-- Inbound webhook -->
          <div
            v-if="selectedType.supportsWebhookTrigger"
            class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined text-amber-600 dark:text-amber-400">webhook</span>
              <div class="text-sm text-amber-900 dark:text-amber-100">
                <p class="font-medium">{{ tt('Inbound webhook', 'Dohodni webhook') }}</p>
                <p class="mt-1">
                  {{
                    tt(
                      'A private webhook URL is created with this function. Leave the sensor input empty to drive it over HTTP, then copy the URL from the function settings after saving.',
                      'S to funkcijo se ustvari zasebni URL webhooka. Če vhod senzorja pustite prazen, funkcijo sprožite prek HTTP, URL pa kopirajte v nastavitvah funkcije po shranjevanju.',
                    )
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Configuration -->
          <div v-if="selectedType.configSchema?.length > 0" class="space-y-4">
            <h3 class="font-semibold text-gray-900 dark:text-white">{{ tt('Configuration', 'Konfiguracija') }}</h3>

            <SunTrackerEditor v-if="isSunTracking" v-model="form.config" />

            <FanTempControllerEditor v-else-if="isFanTempController" v-model="form.config" />

            <FanPresenceTimerEditor v-else-if="isFanPresenceTimer" v-model="form.config" />

            <FanExternalControlEditor v-else-if="isFanExternalControl" v-model="form.config" />

            <CoverExternalControlEditor v-else-if="isCoverExternalControl" v-model="form.config" />

            <TimeSwitchScheduleEditor
              v-else-if="isTimeSwitch"
              :model-value="form.config"
              @update:model-value="form.config = $event" />

            <div v-for="field in displayedConfigFields" :key="field.key" class="space-y-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ field.label }}
                <span v-if="field.required" class="text-red-500">*</span>
                <span v-if="field.unit" class="text-gray-400 font-normal">({{ field.unit }})</span>
              </label>

              <!-- Number input -->
              <div v-if="field.type === 'number'" class="flex items-center gap-2">
                <input
                  v-model.number="form.config[field.key]"
                  type="number"
                  :min="field.min"
                  :max="field.max"
                  :step="field.step || 1"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <!-- Boolean input -->
              <div v-else-if="field.type === 'boolean'" class="flex items-center">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" v-model="form.config[field.key]" class="sr-only peer" />
                  <div
                    class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <!-- Select input -->
              <select
                v-else-if="field.type === 'select'"
                v-model="form.config[field.key]"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                <option
                  v-for="opt in field.options"
                  :key="typeof opt === 'object' ? opt.value : opt"
                  :value="typeof opt === 'object' ? opt.value : opt">
                  {{ typeof opt === 'object' ? opt.label : formatOption(opt) }}
                </option>
              </select>

              <!-- Text/Time input -->
              <input
                v-else
                v-model="form.config[field.key]"
                :type="field.type === 'time' ? 'time' : 'text'"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />

              <p v-if="field.description" class="text-xs text-gray-500 dark:text-gray-400">
                {{ field.description }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
        <button
          v-if="currentStep > 1"
          @click="currentStep--"
          class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
          {{ tt('Back', 'Nazaj') }}
        </button>
        <div v-else></div>

        <div class="flex items-center gap-3">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            {{ tt('Cancel', 'Prekliči') }}
          </button>

          <button
            v-if="currentStep === 1"
            @click="currentStep = 2"
            :disabled="!selectedType"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors">
            {{ tt('Next', 'Naprej') }}
          </button>

          <button
            v-else
            @click="save"
            :disabled="saving || !isValid"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors">
            {{ saving ? tt('Creating...', 'Ustvarjanje...') : tt('Create function', 'Ustvari funkcijo') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { apiFetch } from '../services/api';
import { getTimeSwitchScheduleErrors } from '../utils/switchAutomation';
import { getSunTrackerErrors, normalizeSunTrackerConfig } from '../utils/sunTracker';
import { useShadeSeasonLabels } from '../composables/useShadeSeasonLabels';
import { useI18n } from 'vue-i18n';
import { useToast } from '../composables/useToast';

const { t } = useI18n();
const toast = useToast();
const sunSeasonLabels = useShadeSeasonLabels();
import {
  getFanExternalControlErrors,
  getFanPresenceTimerErrors,
  getFanTempControllerErrors,
  normalizeFanExternalControlConfig,
  normalizeFanPresenceTimerConfig,
  normalizeFanTempControllerConfig,
} from '../utils/fanAutomation';
import { getCoverExternalControlErrors, normalizeCoverExternalControlConfig } from '../utils/coverAutomation';
import DeviceValuePicker from './DeviceValuePicker.vue';
import TimeSwitchScheduleEditor from './devices/TimeSwitchScheduleEditor.vue';
import SunTrackerEditor from './devices/sun/SunTrackerEditor.vue';
import FanTempControllerEditor from './devices/fan/FanTempControllerEditor.vue';
import FanPresenceTimerEditor from './devices/fan/FanPresenceTimerEditor.vue';
import FanExternalControlEditor from './devices/fan/FanExternalControlEditor.vue';
import CoverExternalControlEditor from './devices/cover/CoverExternalControlEditor.vue';
import { useDeviceAutomationsStore } from '../stores/deviceAutomations';
import { tt } from '../utils/consoleText';
import type { Bilingual } from '../config/bilingual';

interface AutomationType {
  automationType: string;
  name: string;
  icon: string;
  description: string;
  requiredBindings: string[];
  optionalBindings: string[];
  bindingMetadata?: Array<{ role: string; localOnly?: boolean; description?: string }>;
  configSchema: ConfigField[];
  supportsWebhookTrigger?: boolean;
}

interface ConfigField {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select' | 'text' | 'time';
  default?: unknown;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: Array<string | { value: string; label: string }>;
  description?: string;
  required?: boolean;
}

const props = defineProps<{
  deviceId: string;
  deviceType: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
}>();

const deviceAutomationsStore = useDeviceAutomationsStore();

const currentStep = ref(1);
const timeSwitchConfigKeys = new Set([
  'schedule_mode',
  'daily_on_time',
  'daily_off_time',
  'daily_schedule',
  'weekly_schedule',
]);
const loadingTypes = ref(true);
const saving = ref(false);
const automationTypes = ref<AutomationType[]>([]);
const selectedType = ref<AutomationType | null>(null);
const bindings = reactive<Record<string, { valueId: string } | null>>({});
const zoneBindings = ref<Array<{ valueId: string } | null>>([null]);
const heatSourceBindings = ref<Array<{ valueId: string } | null>>([null]);

const form = reactive({
  name: '',
  enabled: true,
  config: {} as Record<string, unknown>,
});

const addZone = () => {
  zoneBindings.value.push(null);
};

const removeZone = (index: number) => {
  if (zoneBindings.value.length > 1) {
    zoneBindings.value.splice(index, 1);
  }
};

const addHeatSource = () => {
  heatSourceBindings.value.push(null);
};

const removeHeatSource = (index: number) => {
  if (heatSourceBindings.value.length > 1) {
    heatSourceBindings.value.splice(index, 1);
  }
};

const getTypeIcon = (type: AutomationType) => ({ icon: type.icon || 'settings' });

/**
 * The binding-role vocabulary, in both languages. A data table cannot know the
 * reader's language, so each role carries both halves and `formatBindingRole`
 * picks one through `tt()`. The keys are the roles the automation types declare;
 * the wording is the device vocabulary cluster D's tables use.
 */
const BINDING_ROLE_LABELS: Record<string, Bilingual> = {
  trigger: { en: 'Trigger Input', sl: 'Vhod sprožilca' },
  output: { en: 'Output Control', sl: 'Krmiljenje izhoda' },
  output_tilt: { en: 'Tilt Control', sl: 'Krmiljenje nagiba' },
  brightness: { en: 'Light Sensor', sl: 'Senzor svetlosti' },
  presence: { en: 'Presence Sensor', sl: 'Senzor prisotnosti' },
  condition: { en: 'Condition Input', sl: 'Vhod pogoja' },
  inhibit: { en: 'Inhibit Signal', sl: 'Signal blokade' },
  heat_source: { en: 'Thermostat Output', sl: 'Izhod termostata' },
  demand: { en: 'Water Demand', sl: 'Poraba vode' },
  leak: { en: 'Leak Sensor', sl: 'Senzor puščanja' },
  level: { en: 'Tank Level', sl: 'Nivo rezervoarja' },
  ph: { en: 'pH Reading', sl: 'Meritev pH' },
  orp: { en: 'ORP / Redox', sl: 'ORP / redoks' },
  salinity: { en: 'Salinity', sl: 'Slanost' },
  door: { en: 'Door Contact', sl: 'Kontakt vrat' },
  temperature: { en: 'Temperature Sensor', sl: 'Temperaturni senzor' },
  humidity: { en: 'Humidity Sensor', sl: 'Senzor vlage' },
  speed_up: { en: 'Speed Up Button', sl: 'Tipka za hitreje' },
  speed_down: { en: 'Speed Down Button', sl: 'Tipka za počasneje' },
  open_button: { en: 'Open Button', sl: 'Tipka za odpiranje' },
  close_button: { en: 'Close Button', sl: 'Tipka za zapiranje' },
  stop_button: { en: 'Stop Button', sl: 'Tipka za ustavitev' },
  output_stop: { en: 'Cover Stop', sl: 'Ustavitev senčil' },
  setpoint_input: { en: 'Setpoint Input', sl: 'Vhod želene vrednosti' },
  zone: { en: 'Irrigation Zone', sl: 'Namakalna cona' },
};

/** The plural of a multi-binding role — the zone list's own heading. */
const BINDING_ROLE_PLURALS: Record<string, Bilingual> = {
  zone: { en: 'Irrigation Zones', sl: 'Namakalne cone' },
};

const formatBindingRole = (role: string) => {
  const label = BINDING_ROLE_LABELS[role];
  return label ? tt(label.en, label.sl) : role;
};

const formatBindingRolePlural = (role: string) => {
  const label = BINDING_ROLE_PLURALS[role];
  return label ? tt(label.en, label.sl) : `${formatBindingRole(role)}s`;
};

const formatOption = (opt: string) => {
  return opt.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Check if a binding role is local-only (output must be on parent device) */
const isLocalOnly = (role: string): boolean => {
  return selectedType.value?.bindingMetadata?.some((m) => m.role === role && m.localOnly) ?? false;
};

const isValid = computed(() => {
  if (!form.name.trim()) return false;
  if (!selectedType.value) return false;

  // Check required bindings
  for (const role of selectedType.value.requiredBindings) {
    if (role === 'zone') {
      if (!zoneBindings.value.some((z) => z?.valueId)) return false;
    } else if (role === 'heat_source') {
      if (!heatSourceBindings.value.some((h) => h?.valueId)) return false;
    } else {
      if (!bindings[role]?.valueId) return false;
    }
  }

  if (isTimeSwitch.value && timeSwitchScheduleErrors.value.length > 0) {
    return false;
  }

  if (isSunTracking.value && sunTrackerErrors.value.length > 0) {
    return false;
  }

  if (isFanType.value && fanAutomationErrors.value.length > 0) {
    return false;
  }

  return true;
});

const isTimeSwitch = computed(() => selectedType.value?.automationType === 'time_switch');
const isSunTracking = computed(() => selectedType.value?.automationType === 'shade_sun_tracking');
const isFanTempController = computed(() => selectedType.value?.automationType === 'fan_temperature_controller');
const isFanPresenceTimer = computed(() => selectedType.value?.automationType === 'fan_presence_timer');
const isFanExternalControl = computed(() => selectedType.value?.automationType === 'fan_external_control');
const isCoverExternalControl = computed(() => selectedType.value?.automationType === 'cover_external_control');
const isFanType = computed(
  () =>
    isFanTempController.value || isFanPresenceTimer.value || isFanExternalControl.value || isCoverExternalControl.value,
);
const timeSwitchScheduleErrors = computed(() => (isTimeSwitch.value ? getTimeSwitchScheduleErrors(form.config) : []));
const sunTrackerErrors = computed(() =>
  isSunTracking.value ? getSunTrackerErrors(form.config, sunSeasonLabels.value) : [],
);
const fanAutomationErrors = computed(() => {
  if (isFanTempController.value) return getFanTempControllerErrors(form.config);
  if (isFanPresenceTimer.value) return getFanPresenceTimerErrors(form.config);
  if (isFanExternalControl.value) return getFanExternalControlErrors(form.config);
  if (isCoverExternalControl.value) return getCoverExternalControlErrors(form.config);
  return [];
});
const displayedConfigFields = computed(() => {
  if (!selectedType.value?.configSchema) return [];
  // `hook_id` belongs to the generated webhook URL, not to a form field.
  const fields = selectedType.value.configSchema.filter((field) => field.key !== 'hook_id');
  if (isSunTracking.value || isFanType.value) return [];
  if (!isTimeSwitch.value) return fields;
  return fields.filter((field) => !timeSwitchConfigKeys.has(field.key));
});

/** The handler's own wording for a binding role, when it carries one. */
const bindingDescription = (role: string): string =>
  selectedType.value?.bindingMetadata?.find((meta) => meta.role === role)?.description ?? '';

const selectType = (type: AutomationType) => {
  selectedType.value = type;

  // Reset bindings
  Object.keys(bindings).forEach((key) => delete bindings[key]);
  zoneBindings.value = [null]; // Reset zones
  heatSourceBindings.value = [null]; // Reset heat sources

  // Initialize config with defaults
  form.config = {};
  for (const field of type.configSchema || []) {
    if (field.default !== undefined) {
      form.config[field.key] = field.default;
    }
  }

  // Advanced sun controller: seed the full nested structure (modes + sun window)
  if (type.automationType === 'shade_sun_tracking') {
    form.config = normalizeSunTrackerConfig(form.config) as unknown as Record<string, unknown>;
  }

  // Fan automations: seed the full nested structure
  if (type.automationType === 'fan_temperature_controller') {
    form.config = normalizeFanTempControllerConfig(form.config) as unknown as Record<string, unknown>;
  }
  if (type.automationType === 'fan_presence_timer') {
    form.config = normalizeFanPresenceTimerConfig(form.config) as unknown as Record<string, unknown>;
  }
  if (type.automationType === 'fan_external_control') {
    form.config = normalizeFanExternalControlConfig(form.config) as unknown as Record<string, unknown>;
  }
  if (type.automationType === 'cover_external_control') {
    form.config = normalizeCoverExternalControlConfig(form.config) as unknown as Record<string, unknown>;
  }
};

const fetchTypes = async () => {
  try {
    loadingTypes.value = true;
    automationTypes.value = await apiFetch(`/device-automations/meta/types?deviceType=${props.deviceType}`);
  } catch (err) {
    console.error('Failed to load automation types', err);
  } finally {
    loadingTypes.value = false;
  }
};

const save = async () => {
  if (!selectedType.value || !isValid.value) return;

  try {
    saving.value = true;

    // Build bindings array
    const bindingsArray = [];
    for (const role of [...selectedType.value.requiredBindings, ...selectedType.value.optionalBindings]) {
      if (role === 'zone') {
        // Add all zone bindings
        for (const zone of zoneBindings.value) {
          if (zone?.valueId) {
            bindingsArray.push({ bindingRole: 'zone', valueId: zone.valueId });
          }
        }
      } else if (role === 'heat_source') {
        // Add all heat source bindings
        for (const src of heatSourceBindings.value) {
          if (src?.valueId) {
            bindingsArray.push({ bindingRole: 'heat_source', valueId: src.valueId });
          }
        }
      } else {
        const binding = bindings[role];
        if (binding?.valueId) {
          bindingsArray.push({
            bindingRole: role,
            valueId: binding.valueId,
          });
        }
      }
    }

    await apiFetch('/device-automations', {
      method: 'POST',
      body: JSON.stringify({
        name: form.name.trim(),
        automationType: selectedType.value.automationType,
        enabled: form.enabled,
        config: form.config,
        bindings: bindingsArray,
        deviceId: props.deviceId,
      }),
    });

    // Refresh cached automation bindings so widgets show the new badge
    // immediately instead of requiring a full interface reload.
    deviceAutomationsStore.notifyAutomationsMutated();
    emit('saved');
  } catch (err) {
    console.error('Failed to create automation', err);
    toast.error(t('dialogs.automationCreateFailed'));
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchTypes();
});
</script>
